<script lang="ts">
  import { type AccessArea, AddMemberBodySchema, type Member, type OrgType, type Role, defaultAreasOf } from '@clinic/contracts';
  import { fieldErrors } from '../../../shared/api/errors';
  import { fa } from '../../../shared/i18n/fa';
  import { createForm } from '../../../shared/lib/form.svelte';
  import { online } from '../../../shared/lib/online.svelte';
  import { Button, Card, ErrorAlert, Field, Input } from '../../../shared/ui';
  import { useAddMember, useSetMemberAccess } from '../api/members-api';
  import RolePicker from './RolePicker.svelte';

  let {
    orgId,
    orgType,
    onAdded,
    onCancel,
  }: {
    orgId: string;
    orgType: OrgType;
    /** `warning`: the member was added but their stock access was not saved. */
    onAdded: (member: Member, warning?: string) => void;
    onCancel: () => void;
  } = $props();

  const add = useAddMember(orgId);
  const setAccess = useSetMemberAccess(orgId);
  const form = createForm(() => AddMemberBodySchema, { displayName: '', phone: '', roles: [] as Role[] });
  const serverFields = $derived(fieldErrors(add.error));

  // How far a secretary reaches the stock, decided when they are added (ADR-053); roles that receive
  // goods already (admin, doctor) need no choice. Later it is the member's «دسترسی».
  type InventoryAccess = 'view' | 'receive' | 'receiveCost';
  const INVENTORY_ACCESS: readonly InventoryAccess[] = ['view', 'receive', 'receiveCost'];
  let inventoryAccess = $state<InventoryAccess>('view');
  const roles = $derived(form.values.roles as Role[]);
  const asksInventory = $derived(
    orgType === 'clinic' && roles.length > 0 && defaultAreasOf(roles).includes('inventory') && !defaultAreasOf(roles).includes('inventory_receive'),
  );
  function areasFor(access: InventoryAccess): AccessArea[] {
    const extra: AccessArea[] = access === 'receiveCost' ? ['inventory_receive', 'inventory_cost'] : ['inventory_receive'];
    return [...defaultAreasOf(roles), ...extra];
  }
  const submit = form.submit((body) =>
    add.mutate(body, {
      onSuccess: (member) => {
        if (!asksInventory || inventoryAccess === 'view') return onAdded(member);
        setAccess.mutate(
          { id: member.id, areas: areasFor(inventoryAccess) },
          { onSuccess: (updated) => onAdded(updated), onError: () => onAdded(member, fa.members.inventoryAccess.failed) },
        );
      },
    }),
  );
</script>

<Card class="mb-4">
  <form onsubmit={submit} novalidate class="flex flex-col gap-4">
    <Field label={fa.members.name} hint={fa.members.nameHint} error={form.errors.displayName ?? serverFields.displayName}>
      {#snippet children(a11y)}
        <Input
          {...a11y}
          name="displayName"
          bind:value={form.values.displayName}
          oninput={() => form.touch('displayName')}
          autocomplete="off"
          autofocus
        />
      {/snippet}
    </Field>
    <Field label={fa.members.phone} error={form.errors.phone ?? serverFields.phone}>
      {#snippet children(a11y)}
        <Input
          {...a11y}
          name="phone"
          bind:value={form.values.phone}
          oninput={() => form.touch('phone')}
          type="tel"
          inputmode="tel"
          dir="ltr"
          placeholder={fa.auth.phonePlaceholder}
        />
      {/snippet}
    </Field>
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-text-2">{fa.members.roles}</span>
      <RolePicker
        {orgType}
        value={form.values.roles as Role[]}
        onChange={(roles) => {
          form.values.roles = roles;
          form.touch('roles');
        }}
      />
      {#if form.errors.roles}<p role="alert" class="text-sm text-danger">{form.errors.roles}</p>{/if}
    </div>
    {#if asksInventory}
      <fieldset class="min-w-0">
        <legend class="mb-1.5 text-sm font-medium text-text-2">{fa.members.inventoryAccess.label}</legend>
        <div class="flex flex-col gap-1">
          {#each INVENTORY_ACCESS as a (a)}
            <label class="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-surface-3">
              <input type="radio" name="inventory-access" value={a} checked={inventoryAccess === a} onchange={() => (inventoryAccess = a)} class="size-4 accent-[var(--accent-solid)]" />
              {fa.members.inventoryAccess[a]}
            </label>
          {/each}
        </div>
        <p class="mt-1 text-xs text-text-3">{fa.members.inventoryAccess.hint}</p>
      </fieldset>
    {/if}
    {#if add.error && !Object.keys(serverFields).length}<ErrorAlert error={add.error} />{/if}
    <div class="flex gap-2">
      <Button type="submit" loading={add.isPending || setAccess.isPending} disabled={!online.value}>{fa.members.submitAdd}</Button>
      <Button variant="ghost" onclick={onCancel}>{fa.app.cancel}</Button>
    </div>
  </form>
</Card>
